import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { billableStops } from '../utils/stopKinds';
import EditLoadModal from './EditLoadModal';
import RouteMapModal from './RouteMapModal';
import AIDispatchSuggestions from './AIDispatchSuggestions';
import { logStatusChanged } from '../services/activityLogService';
import { completionBlockText, isRetiredStatus } from '../services/statusTransitions';
import { recalcLoadETAs } from '../services/liveEtaService';
import { fmtDbDateTime, fmtDbTime, fmtWindowDateTime } from '../utils/dbTime';
import { useEquipmentGate, EquipmentIssueList } from './EquipmentGate';   // __EQUIP_COMPAT__
import { checkAssignment } from '../services/equipmentService';
import { truckRequiredOf, TRUCK_REQUIRED, selfUnloadOf, SELF_UNLOAD } from '../utils/equipmentCompat';   // __TRUCK_REQUIRED__ __SELF_UNLOAD__
import { cargoLabel, cargoVocabNow } from '../services/hubCargo';   // __CARGO_V2__ 'Category > type'
import DelayBreakdownPanel from './DelayBreakdownPanel';   // __DELAY_VIEW__ the Delay tab (read-only, from the hub)

// Renders a driver-report photo. Photos live in the private 'load-photos'
// bucket, so a stored object PATH is resolved to a short-lived signed URL; a
// value that is already a full http(s) URL is used as-is (defensive either way).
function ReportPhoto({ value }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!value) return;
      if (/^https?:\/\//i.test(value)) { setSrc(value); return; }
      try {
        const { data } = await supabase.storage.from('load-photos').createSignedUrl(value, 3600);
        if (alive && data?.signedUrl) setSrc(data.signedUrl);
      } catch (e) { /* leave placeholder */ }
    })();
    return () => { alive = false; };
  }, [value]);
  if (!src) {
    return (
      <div style={{ width: '90px', height: '70px', borderRadius: '4px', border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.7rem' }}>photo</div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img src={src} alt="report" style={{ width: '90px', height: '70px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-default)' }} />
    </a>
  );
}

// __PORTAL_PO__ (2026-10-26) the job's PO: billing_data.po (a customer-portal booking, or the Trello card's PO field,
// which the hub's Trello field sync copies onto the job)
function loadPo(load) {
  let bd = load && load.billing_data;
  for (let i = 0; i < 2 && typeof bd === 'string'; i++) { try { bd = JSON.parse(bd || '{}'); } catch (e) { bd = {}; } }
  return bd && bd.po ? String(bd.po) : '';
}

function LoadDetailsModal({ load, onClose, onUpdate }) {
  const [loadStops, setLoadStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // __ATTACH_SIGNED__ signed URLs for this load's attachments, by index.
  // Same idea as ReportPhoto above, but resolved in one place because the
  // renderer needs the URL in two attributes (the link and the thumbnail).
  const [attUrls, setAttUrls] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      const raw = load && load.attachments
        ? (typeof load.attachments === 'string' ? JSON.parse(load.attachments) : load.attachments)
        : [];
      const list = Array.isArray(raw) ? raw : [];
      const out = {};
      for (let i = 0; i < list.length; i++) {
        const att = list[i] || {};
        // A path resolves to a signed URL; anything already absolute is used
        // as-is, which is what every attachment stored before today looks like.
        if (att.storage_path) {
          try {
            const { data } = await supabase.storage
              .from('load-attachments')
              .createSignedUrl(att.storage_path, 3600);
            if (data && data.signedUrl) { out[i] = data.signedUrl; continue; }
          } catch (e) { /* fall through to whatever was stored */ }
        }
        if (att.url) out[i] = att.url;
      }
      if (alive) setAttUrls(out);
    })();
    return () => { alive = false; };
  }, [load]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRouteMap, setShowRouteMap] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [gpsTrail, setGpsTrail] = useState([]);
  const [geofenceEvents, setGeofenceEvents] = useState([]);
  const [loadingTrail, setLoadingTrail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableTrucks, setAvailableTrucks] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(load.driver_id || '');
  const [selectedTruckId, setSelectedTruckId] = useState(load.truck_id || '');
  const [liveEtas, setLiveEtas] = useState(null);
  const [liveEtaGps, setLiveEtaGps] = useState(null);
  const [liveEtaLoading, setLiveEtaLoading] = useState(false);
  const [liveEtaErr, setLiveEtaErr] = useState(null);
  // __EQUIP_COMPAT__ hard equipment warning on Quick Assign + a live hint for the picked driver/truck
  const [gateEquipment, equipmentModal] = useEquipmentGate();
  const [equipHint, setEquipHint] = useState(null);
  useEffect(() => {
    let alive = true;
    setEquipHint(null);
    if (!selectedDriverId) return undefined;
    checkAssignment({ driverId: selectedDriverId, loads: [load], truckId: selectedTruckId || null })
      .then((r) => { if (alive) setEquipHint(r); })
      .catch(() => {});
    return () => { alive = false; };
  }, [selectedDriverId, selectedTruckId, load]);

  const statusWorkflow = {
    'pending':       ['assigned', 'en_route', 'complete'],
    'assigned':      ['en_route', 'at_pickup', 'in_transit', 'complete', 'pending'],
    'en_route':      ['at_pickup', 'loading', 'in_transit', 'complete', 'assigned'],
    'at_pickup':     ['loading', 'in_transit', 'complete', 'en_route'],
    'loading':       ['in_transit', 'at_delivery', 'complete', 'at_pickup'],
    'in_transit':    ['at_delivery', 'unloading', 'complete', 'loading'],
    'at_delivery':   ['unloading', 'complete', 'in_transit'],
    'unloading':     ['pending_completion', 'complete', 'at_delivery'],
    // Set by the geofence engine when the truck leaves the delivery: dispatch
    // confirms (complete) or sends it back if the driver left early.
    'pending_completion': ['complete', 'unloading', 'at_delivery'],
    // __RTB_RETIRED__ a complete job IS ready to bill (Henry 2026-09-30). The hub's
    // Hauling Board tab bills it and marks it billed, so the board has no step after it.
    'complete':      [],
    // Retired status: an old row can only be moved to Complete.
    'ready_to_bill': ['complete'],
    'billed':        []
  };

  const statusLabels = {
    'pending':       'Pending',
    'assigned':      'Assigned',
    'en_route':      'En Route',
    'at_pickup':     'At Pickup',
    'loading':       'Loading',
    'in_transit':    'In Transit',
    'at_delivery':   'At Delivery',
    'unloading':     'Unloading',
    'pending_completion': 'Confirm Complete',
    'complete':      'Complete',
    'ready_to_bill': 'Complete (was Ready to Bill)',
    'billed':        'Billed'
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending':       'var(--color-warning)',
      'assigned':      'var(--accent-blue)',
      'en_route':      'var(--color-info)',
      'at_pickup':     'var(--color-warning)',
      'loading':       'var(--color-danger)',
      'in_transit':    'var(--accent-primary)',
      'at_delivery':   'var(--color-success)',
      'unloading':     'var(--color-success)',
      'pending_completion': '#f85149',
      'complete':      'var(--accent-primary)',
      'ready_to_bill': 'var(--accent-primary)',
      'billed':        '#a371f7'
    };
    return colors[status] || 'var(--text-muted)';
  };

  const statusLabel = (status) => statusLabels[status] || (status || '').replace(/_/g, ' ');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: stopsData, error: stopsError } = await supabase
          .from('load_stops')
          .select('*')
          .eq('load_id', load.id)
          .order('stop_type', { ascending: true })
          .order('stop_sequence', { ascending: true });
        if (stopsError) throw stopsError;
        setLoadStops(stopsData || []);

        // Drivers — join users table for name
        const { data: driversData } = await supabase
          .from('drivers')
          .select('id, name, phone, email, user_id, user:user_id(name)')   // __DRIVER_NAME_FALLBACK__ drivers.name for login-less drivers
          .order('id');
        setAvailableDrivers(driversData || []);

        const { data: trucksData } = await supabase
          .from('trucks')
          .select('*')
          .eq('is_active', true)
          .order('truck_number');
        setAvailableTrucks(trucksData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [load.id]);

  const pickupStops = loadStops.filter(s => s.stop_type === 'pickup');
  const deliveryStops = loadStops.filter(s => s.stop_type === 'delivery');
  const stopLabelById = {};
  loadStops.forEach(s => {
    stopLabelById[s.id] = `#${s.stop_sequence} ${(s.address || '').split(',')[0]}`.trim();
  });

  const formatDateTime = (ds) => {
    if (!ds) return 'Not set';
    // __TZ_WINDOWS__ only time windows come here: Toronto wall clock, shown as such
    // (the same hours as the hub, Trello and the apps). parseDbTime read them as UTC.
    return fmtWindowDateTime(ds) || 'Not set';
  };

  const handleStatusChange = async (newStatus) => {
    if (isRetiredStatus(newStatus)) {
      setError('That status is no longer used - a complete job is already ready to bill.');
      return;
    }
    setStatusUpdating(true);
    setError(null);
    try {
      // Optimistic guard: only apply if the load is still in the status this
      // modal was opened on. The driver app and the geofence engine write the
      // same column; a blind update was last-writer-wins (audit S15).
      const { data: updatedRows, error: updateError } = await supabase
        .from('loads')
        .update({ status: newStatus })
        .eq('id', load.id)
        .eq('status', load.status)
        .select('id');
      if (updateError) throw updateError;
      if (!updatedRows || updatedRows.length === 0) {
        const { data: fresh } = await supabase.from('loads').select('status').eq('id', load.id).single();
        const now = fresh && fresh.status ? statusLabel(fresh.status) : 'a different status';
        setError(`Not applied: this load is already "${now}" (changed by the driver app or the geofence). Close and reopen to see the current state.`);
        if (onUpdate) onUpdate();
        return;
      }
      if (load.driver_id) {
        try {
          const { notifyStatusChange } = await import('../services/pushNotificationService');
          await notifyStatusChange(load.driver_id, load, newStatus);
        } catch (e) { console.warn('Push failed:', e); }
      }
      logStatusChanged(load, load.status, newStatus);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(completionBlockText(err) || err.message);   // __CARGO_REQUIRED__
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleQuickAssign = async () => {
    setAssigning(true);
    setError(null);
    try {
      if ((load.driver_id || null) !== (selectedDriverId || null)) {
        const { guardReassignAllowed } = await import('../services/routingEngineService');
        const g = await guardReassignAllowed(load.id);
        if (g.tier === 'blocked') { setError(g.reason); return; }
        if (g.tier === 'override' && !window.confirm('WARNING: ' + g.reason + '\n\nOverride and reassign anyway?')) { return; }
      }
      // __EQUIP_COMPAT__ the driver's (or the picked truck's) equipment vs this job - before anything is written
      if (selectedDriverId) {
        const eq = await gateEquipment({ driverId: selectedDriverId, loads: [load], truckId: selectedTruckId || null, context: 'quick_assign', title: 'Quick Assign' });
        if (!eq.proceed) return;
      }
      const { error: updateError } = await supabase
        .from('loads')
        .update({
          driver_id: selectedDriverId || null,
          truck_id: selectedTruckId || null,
          status: selectedDriverId ? 'assigned' : load.status
        })
        .eq('id', load.id);
      if (updateError) throw updateError;
      if ((load.driver_id || null) !== (selectedDriverId || null)) {
        try {
          const { reconcileRoutesAfterReassignment } = await import('../services/routingEngineService');
          await reconcileRoutesAfterReassignment({ loadId: load.id, oldDriverId: load.driver_id || null, newDriverId: selectedDriverId || null, scheduledDate: load.scheduled_date || null });
        } catch (e) { console.warn('route reconcile failed (non-fatal):', e); }
        // __JOB_CHANGES__ the driver who loses the job is told (the new one gets the assignment push below)
        try {
          const { reportJobChanged } = await import('../services/hubPlanning');
          reportJobChanged(load.id, load, null, 'Load Details', { new_driver_told: !!selectedDriverId });
        } catch (e) { console.warn('job change not reported:', e); }
      }
      if (selectedDriverId) {
        try {
          const { notifyLoadAssigned } = await import('../services/pushNotificationService');
          await notifyLoadAssigned(selectedDriverId, load);
        } catch (e) { console.warn('Push failed:', e); }
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      const { error: stopsError } = await supabase
        .from('load_stops').delete().eq('load_id', load.id);
      if (stopsError) throw stopsError;
      const { error: loadError } = await supabase
        .from('loads').delete().eq('id', load.id);
      if (loadError) throw loadError;
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const generateLoadNumber = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `L${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}-${rand}`;
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: newLoad, error: loadError } = await supabase
        .from('loads')
        .insert([{
          load_number: generateLoadNumber(),
          customer_id: load.customer_id,
          driver_id: null,
          truck_id: null,
          commodity: load.commodity,
          weight_kg: load.weight_kg,
          length_cm: load.length_cm,
          width_cm: load.width_cm,
          height_cm: load.height_cm,
          status: 'pending',
          notes: load.notes
            ? `(Duplicated from ${load.load_number}) ${load.notes}`
            : `Duplicated from ${load.load_number}`,
          created_by_user_id: user.id,
          // __OPEN_DECK__ a copy is the same kind of job: it keeps the Truck required (and the self-unload way) - nothing
          // else of billing_data (prices, overrides, the Trello link) is copied
          ...((truckRequiredOf(load) || selfUnloadOf(load)) ? { billing_data: JSON.stringify({
            ...(truckRequiredOf(load) ? { truck_required: truckRequiredOf(load) } : {}),
            ...(selfUnloadOf(load) ? { self_unload: selfUnloadOf(load) } : {}) }) } : {})
        }])
        .select()
        .single();
      if (loadError) throw loadError;
      // __DRIVER_STOP_UNBILLED__ a duplicate is a new CUSTOMER job: the driver's fuel/break
      // stops of the old one are its history, not part of the new job.
      const customerStops = billableStops(loadStops);
      if (customerStops.length > 0) {
        const { error: stopsError } = await supabase
          .from('load_stops')
          .insert(customerStops.map(s => ({
            load_id: newLoad.id,
            stop_type: s.stop_type,
            stop_sequence: s.stop_sequence,
            address: s.address,
            time_window_start: s.time_window_start,
            time_window_end: s.time_window_end,
            notes: s.notes
          })));
        if (stopsError) throw stopsError;
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setDuplicating(false);
      setConfirmDuplicate(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const currentAttachments = load.attachments
        ? (typeof load.attachments === 'string' ? JSON.parse(load.attachments) : load.attachments)
        : [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `loads/${load.id}/${Date.now()}_${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from('load-attachments')
          .upload(storagePath, file, { contentType: file.type });
        if (uploadError) { console.error('Upload error:', uploadError); continue; }
        // __ATTACH_SIGNED__ no permanent public URL is stored. The path is
        // the durable reference; the URL is minted per view and expires. A
        // public URL written into a row is a link that outlives any decision
        // to make the bucket private.
        currentAttachments.push({
          name: file.name,
          type: file.type,
          uploaded_at: new Date().toISOString(),
          uploaded_from: 'hauling',
          storage_path: storagePath
        });
      }
      await supabase.from('loads').update({ attachments: currentAttachments }).eq('id', load.id);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { setError('Please allow popups to print'); return; }
    const printContent = `<!DOCTYPE html><html><head><title>Load #${load.load_number}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;max-width:800px;margin:0 auto}
      h1{color:#333;border-bottom:2px solid #16c79a;padding-bottom:10px}
      h2{color:#555;margin-top:20px}.section{margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:5px}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
      .label{color:#888;font-size:12px;text-transform:uppercase}.value{color:#333;font-size:14px;margin-top:5px}
      .stop{padding:10px;border-left:3px solid #16c79a;margin-bottom:10px;background:#fff}
      .stop-pickup{border-left-color:#e67e22}.stop-delivery{border-left-color:#27ae60}
      .footer{margin-top:30px;padding-top:20px;border-top:1px solid #ddd;font-size:12px;color:#888}
      @media print{body{padding:0}.section{break-inside:avoid}}</style></head><body>
      <h1>Load #${load.load_number}</h1>
      <div class="grid">
        <div class="section"><div class="label">Customer</div>
          <div class="value">${load.customer?.company_name || load.customer_name || 'Unknown'}</div>
          ${load.customer?.phone ? `<div class="value">${load.customer.phone}</div>` : ''}
        </div>
        <div class="section"><div class="label">Cargo</div>
          <div class="value">${cargoLabel(cargoVocabNow(), load.commodity) || 'N/A'}</div>
          <div class="value">${load.weight_kg ? load.weight_kg + ' kg' : ''} ${load.length_cm ? '| ' + load.length_cm + 'x' + (load.width_cm||'?') + 'x' + (load.height_cm||'?') + ' cm' : ''}</div>
        </div>
        <div class="section"><div class="label">Driver</div>
          <div class="value">${load.driver?.user_id?.name || 'Not assigned'}</div>
          ${load.driver?.phone ? `<div class="value">${load.driver.phone}</div>` : ''}
        </div>
        <div class="section"><div class="label">Truck</div>
          <div class="value">${load.truck ? 'Truck ' + load.truck.truck_number : 'Not assigned'}</div>
          ${load.truck?.license_plate ? `<div class="value">${load.truck.license_plate}</div>` : ''}
        </div>
      </div>
      <h2>Pickup Stops (${pickupStops.length})</h2>
      ${pickupStops.map(s => `<div class="stop stop-pickup"><strong>#${s.stop_sequence}: ${s.address}</strong>
        ${s.time_window_start ? '<div>From: ' + formatDateTime(s.time_window_start) + '</div>' : ''}
        ${s.notes ? '<div><em>' + s.notes + '</em></div>' : ''}</div>`).join('') || '<p>None</p>'}
      <h2>Delivery Stops (${deliveryStops.length})</h2>
      ${deliveryStops.map(s => `<div class="stop stop-delivery"><strong>#${s.stop_sequence}: ${s.address}</strong>
        ${s.time_window_start ? '<div>From: ' + formatDateTime(s.time_window_start) + '</div>' : ''}
        ${s.notes ? '<div><em>' + s.notes + '</em></div>' : ''}</div>`).join('') || '<p>None</p>'}
      ${load.notes ? '<h2>Notes</h2><div class="section"><div class="value">' + load.notes + '</div></div>' : ''}
      <div class="footer">Printed from HMGC Hauling Board on ${new Date().toLocaleString()}</div>
      </body></html>`;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Realtime subscription for comments + status changes
  useEffect(() => {
    const channel = supabase
      .channel(`load-${load.id}-comments`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'load_comments',
        filter: `load_id=eq.${load.id}`,
      }, () => {
        fetchComments();
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'loads',
        filter: `id=eq.${load.id}`,
      }, (payload) => {
        if (onUpdate) onUpdate(payload.new);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [load.id]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const { data } = await supabase.from('load_comments')
        .select('*').eq('load_id', load.id)
        .order('created_at', { ascending: false }).limit(100);
      setComments(data || []);
    } catch(e) { console.error('Comments error:', e); }
    finally { setLoadingComments(false); }
  };

  const fetchTrail = async () => {
    setLoadingTrail(true);
    try {
      // Get driver_id and time range from load
      const driverId = load.driver_id;
      const startedAt = load.started_at;
      const completedAt = load.completed_at;
      if (!driverId) { setLoadingTrail(false); return; }

      // GPS trail
      let trailQuery = supabase.from('gps_locations')
        .select('latitude,longitude,speed_kmh,heading,timestamp')
        .eq('driver_id', driverId)
        .order('timestamp', { ascending: true })
        .limit(2000);
      if (startedAt) trailQuery = trailQuery.gte('timestamp', startedAt);
      if (completedAt) trailQuery = trailQuery.lte('timestamp', completedAt);
      const { data: trail } = await trailQuery;
      setGpsTrail(trail || []);

      // Geofence events for this load
      const { data: gfEvents } = await supabase.from('geofence_events')
        .select('*').eq('load_id', load.id)
        .order('created_at', { ascending: true });
      setGeofenceEvents(gfEvents || []);
    } catch(e) { console.error('Trail error:', e); }
    finally { setLoadingTrail(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'comments' && comments.length === 0) fetchComments();
    if (tab === 'trail') fetchTrail();
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      await supabase.from('load_comments').insert({
        load_id: load.id,
        author_name: 'Dispatcher',
        comment: newComment.trim(),
        is_system: false,
      });
      setNewComment('');
      await fetchComments();
    } catch(e) { console.error('Post comment error:', e); }
    finally { setPostingComment(false); }
  };

  const nextStatuses = statusWorkflow[load.status] || [];
  const handleRecalcLiveEtas = async () => {
    setLiveEtaLoading(true);
    setLiveEtaErr(null);
    try {
      const res = await recalcLoadETAs({ load, loadStops });
      if (res.success) {
        setLiveEtas(res.stops);
        setLiveEtaGps(res.gps);
      } else {
        setLiveEtas(null);
        setLiveEtaErr(
          res.reason === 'no_gps' ? 'No GPS ping from this load’s truck yet.' :
          res.reason === 'gps_stale' ? res.message :
          res.reason === 'no_remaining' ? 'All stops complete — nothing to project.' :
          res.reason === 'no_coords' ? 'Stops are missing map coordinates.' :
          'Could not compute live ETAs.'
        );
      }
    } catch (e) {
      setLiveEtaErr('Could not compute live ETAs.');
    } finally {
      setLiveEtaLoading(false);
    }
  };

  const assignChanged = selectedDriverId !== (load.driver_id || '') || selectedTruckId !== (load.truck_id || '');

  const sectionStyle = { backgroundColor: 'var(--surface-2)', padding: '1rem', borderRadius: '5px', marginBottom: '1.5rem' };
  const sectionHeader = { color: 'var(--accent-primary)', margin: '0 0 0.75rem 0', fontSize: '1rem' };
  const btnBase = { border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', padding: '0.5rem 1rem' };

  const attachments = load.attachments
    ? (typeof load.attachments === 'string' ? JSON.parse(load.attachments) : load.attachments)
    : [];

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ backgroundColor: 'var(--surface-1)', borderRadius: '10px', padding: '2rem', maxWidth: '800px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--surface-2)' }}>
          <div>
            <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.5rem 0' }}>Load #{load.load_number}</h2>
            <span style={{ display: 'inline-block', padding: '0.25rem 0.75rem', backgroundColor: getStatusColor(load.status), borderRadius: '15px', fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
              {statusLabel(load.status)}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', padding: '1rem', borderRadius: '5px', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{error}</span>
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
          </div>
        )}

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--surface-2)', paddingBottom: '0' }}>
          {[['details','📋 Details'],['delay','⏱ Delay'],['comments','💬 Comments'],['trail','🛰 GPS Trail']].map(([id, label]) => (
            <button key={id} onClick={() => handleTabChange(id)}
              style={{ background: 'none', border: 'none', borderBottom: activeTab === id ? '2px solid var(--accent-primary)' : '2px solid transparent',
                color: activeTab === id ? 'var(--accent-primary)' : 'var(--text-muted)',
                padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '-2px' }}>
              {label}
            </button>
          ))}
        </div>

        {/* __DELAY_VIEW__ Delay Tab: the job's delay by type, billable vs recorded only (the hub's billing numbers) */}
        {activeTab === 'delay' && <DelayBreakdownPanel loadId={load.id} />}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                value={newComment} onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handlePostComment()}
                placeholder="Add a comment..."
                style={{ flex: 1, padding: '0.6rem', backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-default)', borderRadius: '5px', color: 'var(--text-primary)', fontSize: '0.9rem' }}
              />
              <button onClick={handlePostComment} disabled={postingComment || !newComment.trim()}
                style={{ padding: '0.6rem 1.2rem', backgroundColor: 'var(--accent-primary)', color: 'var(--bg-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', opacity: !newComment.trim() ? 0.4 : 1 }}>
                {postingComment ? '...' : 'Post'}
              </button>
            </div>
            {loadingComments ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading...</div>
            ) : comments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No comments yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {comments.map(c => (
                  <div key={c.id} style={{ padding: '0.75rem', backgroundColor: c.is_system ? 'var(--surface-1)' : 'var(--surface-2)', borderRadius: '5px', border: '1px solid var(--border-default)', opacity: c.is_system ? 0.8 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: c.is_system ? 'var(--text-muted)' : 'var(--accent-primary)' }}>
                        {c.is_system ? '⚙ System' : c.author_name}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                    </div>
                    {c.report_type && (
                      <div style={{ marginBottom: '0.35rem' }}>
                        <span style={{
                          display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '3px',
                          fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px',
                          color: 'var(--text-primary)',
                          backgroundColor: c.report_type === 'issue' ? 'var(--color-danger)' : c.report_type === 'damage' ? 'var(--color-warning)' : 'var(--accent-blue)'
                        }}>
                          {c.report_type === 'issue' ? '⚠ Issue' : c.report_type === 'damage' ? '🛠 Damage' : '📝 Note'}
                        </span>
                        {c.load_stop_id && stopLabelById[c.load_stop_id] && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            at {stopLabelById[c.load_stop_id]}
                          </span>
                        )}
                      </div>
                    )}
                    {c.status_transition && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginBottom: '0.25rem', fontStyle: 'italic' }}>{c.status_transition}</div>
                    )}
                    <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{c.comment}</div>
                    {Array.isArray(c.photo_urls) && c.photo_urls.length > 0 && (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        {c.photo_urls.map((p, idx) => (
                          <ReportPhoto key={idx} value={p} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trail Tab */}
        {activeTab === 'trail' && (
          <div>
            {loadingTrail ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-primary)' }}>Loading GPS trail...</div>
            ) : !load.driver_id ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>No driver assigned to this load.</div>
            ) : gpsTrail.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                {!load.started_at ? 'Load has not started yet — no GPS trail available.' : 'No GPS data recorded for this load.'}
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  <span>📍 {gpsTrail.length} GPS pings</span>
                  {load.started_at && <span>▶ Started: {fmtDbDateTime(load.started_at)}</span>}
                  {load.completed_at && <span>✓ Completed: {fmtDbDateTime(load.completed_at)}</span>}
                </div>

                {/* Geofence Events Timeline */}
                {geofenceEvents.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '0.75rem', fontSize: '0.9rem', letterSpacing: '1px' }}>GEOFENCE EVENTS</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {geofenceEvents.map(ev => {
                        const icons = { enter: '📍', dwell: '⏱️', exit: '🚛', drive_by: '⚠️', gps_gap: '📵', completion_pending: '📦', break_movement: '🚨' };
                        const colors = { enter: 'var(--accent-primary)', dwell: 'var(--color-warning)', exit: 'var(--color-success)', drive_by: 'var(--color-danger)', gps_gap: 'var(--color-danger)', completion_pending: '#f0883e' };
                        return (
                          <div key={ev.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.5rem', backgroundColor: 'var(--surface-2)', borderRadius: '4px', borderLeft: `3px solid ${colors[ev.event_type] || 'var(--border-default)'}` }}>
                            <span style={{ fontSize: '1rem' }}>{icons[ev.event_type] || '📌'}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: colors[ev.event_type] || 'var(--text-muted)', textTransform: 'uppercase' }}>{ev.event_type.replace(/_/g,' ')}</span>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(ev.created_at).toLocaleTimeString()}</span>
                              </div>
                              {ev.address && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{ev.address}</div>}
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: '0.75rem' }}>
                                {ev.distance_meters && <span>{Math.round(ev.distance_meters)}m away</span>}
                                {ev.dwell_seconds > 0 && <span>Dwell: {Math.round(ev.dwell_seconds/60)}min</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* GPS Ping Summary Table */}
                <div>
                  <h4 style={{ color: 'var(--accent-primary)', marginBottom: '0.75rem', fontSize: '0.9rem', letterSpacing: '1px' }}>GPS PING LOG</h4>
                  <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: '5px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--surface-3)' }}>
                        <tr>
                          {['Time','Lat','Lng','Speed','Heading'].map(h => (
                            <th key={h} style={{ padding: '0.4rem 0.6rem', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 'bold', borderBottom: '1px solid var(--border-default)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {gpsTrail.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--surface-2)' }}>
                            <td style={{ padding: '0.3rem 0.6rem', color: 'var(--text-secondary)' }}>{fmtDbTime(p.timestamp)}</td>
                            <td style={{ padding: '0.3rem 0.6rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>{p.latitude?.toFixed(5)}</td>
                            <td style={{ padding: '0.3rem 0.6rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>{p.longitude?.toFixed(5)}</td>
                            <td style={{ padding: '0.3rem 0.6rem', color: 'var(--accent-primary)' }}>{p.speed_kmh ? `${Math.round(p.speed_kmh)} km/h` : '—'}</td>
                            <td style={{ padding: '0.3rem 0.6rem', color: 'var(--text-secondary)' }}>{p.heading ? `${Math.round(p.heading)}°` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Details Tab — original content */}
        {activeTab === 'details' && <div>

        {/* Status Actions */}
        {nextStatuses.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={sectionHeader}>Update Status</h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {nextStatuses.map((s) => (
                <button key={s} onClick={() => handleStatusChange(s)} disabled={statusUpdating}
                  style={{ ...btnBase, backgroundColor: statusUpdating ? 'var(--surface-3)' : getStatusColor(s), color: 'var(--text-primary)', opacity: statusUpdating ? 0.6 : 1, cursor: statusUpdating ? 'not-allowed' : 'pointer' }}>
                  {statusUpdating ? 'Updating...' : `→ ${statusLabels[s]}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Customer & Cargo */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={sectionStyle}>
            <h3 style={sectionHeader}>Customer</h3>
            <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {load.customer?.company_name || load.customer_name || 'Unknown Customer'}
            </div>
            {load.customer?.phone && <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{load.customer.phone}</div>}
            {loadPo(load) && <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>PO: <strong>{loadPo(load)}</strong></div>}
          </div>
          <div style={sectionStyle}>
            <h3 style={sectionHeader}>Cargo</h3>
            <div style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>{cargoLabel(cargoVocabNow(), load.commodity)}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {load.weight_kg && `${load.weight_kg} kg`}
              {load.length_cm && ` | ${load.length_cm}×${load.width_cm || '?'}×${load.height_cm || '?'} cm`}
            </div>
            {/* __TRUCK_REQUIRED__ (2026-10-06) set at booking; shown here, in the opened job, not on the card front */}
            {truckRequiredOf(load) && (
              <div data-truck-required style={{ marginTop: '0.5rem', color: 'var(--color-warning)', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Truck required: {TRUCK_REQUIRED[truckRequiredOf(load)]}
              </div>
            )}
            {/* __SELF_UNLOAD__ (2026-10-08) set at booking: truck crane, or the site's equipment run by our driver */}
            {selfUnloadOf(load) && (
              <div data-self-unload style={{ marginTop: '0.5rem', color: 'var(--color-warning)', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Self-unload: {SELF_UNLOAD[selfUnloadOf(load)]}
              </div>
            )}
          </div>
        </div>

        {/* Quick Assign */}
        <div style={sectionStyle}>
          <h3 style={sectionHeader}>Quick Assign</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.85rem' }}>Driver</label>
              <select value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', backgroundColor: 'var(--surface-1)', border: '1px solid var(--border-default)', borderRadius: '5px', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                <option value="">No driver</option>
                {availableDrivers.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.user?.name || d.name || d.email || `Driver ${d.id.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.85rem' }}>Truck</label>
              <select value={selectedTruckId} onChange={(e) => setSelectedTruckId(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', backgroundColor: 'var(--surface-1)', border: '1px solid var(--border-default)', borderRadius: '5px', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                <option value="">No truck</option>
                {availableTrucks.map(t => (
                  <option key={t.id} value={t.id}>Truck {t.truck_number} ({t.license_plate})</option>
                ))}
              </select>
            </div>
            <button onClick={handleQuickAssign} disabled={assigning || !assignChanged}
              style={{ ...btnBase, backgroundColor: (assigning || !assignChanged) ? 'var(--surface-3)' : 'var(--color-warning)', color: 'var(--text-primary)', cursor: (assigning || !assignChanged) ? 'not-allowed' : 'pointer', opacity: (assigning || !assignChanged) ? 0.6 : 1 }}>
              {assigning ? 'Saving...' : 'Assign'}
            </button>
            <button onClick={() => setShowAISuggestions(true)}
              style={{ ...btnBase, backgroundColor: 'var(--color-info)', color: 'var(--text-primary)', cursor: 'pointer' }}>
              AI Suggest
            </button>
          </div>
          {selectedDriverId && equipHint && (equipHint.issues.length > 0 || equipHint.unchecked) && (
            <div style={{ marginTop: '0.75rem', padding: '0.5rem', borderRadius: '5px', backgroundColor: 'var(--surface-1)', borderLeft: '4px solid ' + (equipHint.canAssign && !equipHint.needsReason ? 'var(--color-warning)' : 'var(--color-danger)') }}>
              <EquipmentIssueList result={equipHint} compact />
            </div>
          )}
          <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Current: {load.driver?.user_id?.name || 'No driver'} | {load.truck ? `Truck ${load.truck.truck_number}` : 'No truck'}
          </div>
        </div>

        {/* Stops */}
        {loading ? (
          <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>Loading stops...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={sectionStyle}>
              <h3 style={{ ...sectionHeader, color: 'var(--color-warning)' }}>Pickup Stops ({pickupStops.length})</h3>
              {pickupStops.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>None</div> :
                pickupStops.map((stop, i) => (
                  <div key={stop.id} style={{ marginBottom: i < pickupStops.length - 1 ? '1rem' : 0, paddingBottom: i < pickupStops.length - 1 ? '1rem' : 0, borderBottom: i < pickupStops.length - 1 ? '1px solid var(--border-default)' : 'none' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>#{stop.stop_sequence}: {stop.address}</div>
                    {(stop.time_window_start || stop.time_window_end) && (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {stop.time_window_start && `From: ${formatDateTime(stop.time_window_start)}`}
                        {stop.time_window_end && ` | To: ${formatDateTime(stop.time_window_end)}`}
                      </div>
                    )}
                    {stop.notes && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>{stop.notes}</div>}
                  </div>
                ))
              }
            </div>
            <div style={sectionStyle}>
              <h3 style={{ ...sectionHeader, color: 'var(--color-success)' }}>Delivery Stops ({deliveryStops.length})</h3>
              {deliveryStops.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>None</div> :
                deliveryStops.map((stop, i) => (
                  <div key={stop.id} style={{ marginBottom: i < deliveryStops.length - 1 ? '1rem' : 0, paddingBottom: i < deliveryStops.length - 1 ? '1rem' : 0, borderBottom: i < deliveryStops.length - 1 ? '1px solid var(--border-default)' : 'none' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>#{stop.stop_sequence}: {stop.address}</div>
                    {(stop.time_window_start || stop.time_window_end) && (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {stop.time_window_start && `From: ${formatDateTime(stop.time_window_start)}`}
                        {stop.time_window_end && ` | To: ${formatDateTime(stop.time_window_end)}`}
                      </div>
                    )}
                    {stop.notes && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>{stop.notes}</div>}
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Live ETAs */}
        {load.driver_id && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: liveEtas ? '0.75rem' : 0 }}>
              <h3 style={{ ...sectionHeader, margin: 0 }}>Live ETAs</h3>
              <button onClick={handleRecalcLiveEtas} disabled={liveEtaLoading}
                style={{ ...btnBase, backgroundColor: 'var(--color-info)', color: 'var(--text-primary)', cursor: liveEtaLoading ? 'not-allowed' : 'pointer', opacity: liveEtaLoading ? 0.6 : 1, fontSize: '0.85rem' }}>
                {liveEtaLoading ? 'Calculating…' : 'Recalculate from live GPS'}
              </button>
            </div>
            {liveEtaErr && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{liveEtaErr}</div>}
            {liveEtas && liveEtas.length > 0 && (
              <div>
                {liveEtaGps && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    From GPS {liveEtaGps.ageMinutes != null ? `${liveEtaGps.ageMinutes}m old` : ''}{liveEtaGps.status ? ` (${liveEtaGps.status})` : ''} · plan order across the driver’s loads · time already driven since the ping is credited
                  </div>
                )}
                {liveEtas.map((s) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid var(--border-default)' }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                      <span style={{ textTransform: 'uppercase', fontSize: '0.7rem', color: s.stop_type === 'pickup' ? 'var(--color-warning)' : 'var(--color-success)', marginRight: '0.5rem' }}>{s.stop_type} #{s.stop_sequence}</span>
                      {s.address ? s.address.split(',')[0] : ''}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>~{s.etaFormatted}</span>
                      {s.deltaMinutes !== null && s.deltaMinutes !== undefined && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: s.deltaMinutes > 20 ? 'var(--color-danger)' : s.deltaMinutes > 5 ? 'var(--color-warning)' : 'var(--color-success)' }}>
                          {s.deltaMinutes > 0 ? `${s.deltaMinutes}m late` : s.deltaMinutes < 0 ? `${-s.deltaMinutes}m early` : 'on time'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {load.notes && (
          <div style={sectionStyle}>
            <h3 style={sectionHeader}>Notes</h3>
            <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{load.notes}</div>
          </div>
        )}

        {/* Attachments */}
        <div style={sectionStyle}>
          <h3 style={sectionHeader}>Attachments</h3>
          {attachments.length > 0 ? (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {attachments.map((att, idx) => (
                <a key={idx} href={attUrls[idx] || att.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', padding: '0.5rem', backgroundColor: 'var(--surface-1)', border: '1px solid var(--border-default)', borderRadius: '5px', textDecoration: 'none', maxWidth: '120px' }}>
                  {att.type && att.type.startsWith('image/') ? (
                    <img src={attUrls[idx] || att.url} alt={att.name} style={{ width: '100px', height: '80px', objectFit: 'cover', borderRadius: '3px' }} />
                  ) : (
                    <div style={{ width: '100px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--surface-3)', borderRadius: '3px', fontSize: '2rem' }}>📄</div>
                  )}
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{att.name}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{att.origin === 'customer portal' ? 'Customer' : att.uploaded_from === 'billing' ? 'Billing' : 'Hauling'}</span>
                </a>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>No attachments</div>
          )}
          <input type="file" id="haulingAttachmentInput" multiple accept="image/*,.pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleAttachmentUpload} />
          <button onClick={() => document.getElementById('haulingAttachmentInput').click()} disabled={uploading}
            style={{ ...btnBase, backgroundColor: 'var(--btn-secondary-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', opacity: uploading ? 0.6 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? 'Uploading...' : '+ Add Attachment'}
          </button>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} disabled={deleting}
                style={{ ...btnBase, padding: '0.75rem 1.5rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', fontSize: '1rem', opacity: deleting ? 0.6 : 1, cursor: deleting ? 'not-allowed' : 'pointer' }}>
                Delete Load
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Sure?</span>
                <button onClick={handleDelete} disabled={deleting}
                  style={{ ...btnBase, padding: '0.75rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                  {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  style={{ ...btnBase, padding: '0.75rem 1rem', backgroundColor: 'var(--btn-secondary-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', fontSize: '0.9rem' }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button onClick={() => setShowRouteMap(true)}
              style={{ ...btnBase, padding: '0.75rem 1.5rem', backgroundColor: 'var(--accent-primary)', color: 'var(--text-primary)', fontSize: '1rem' }}>
              Route Map
            </button>
            <button onClick={handlePrint}
              style={{ ...btnBase, padding: '0.75rem 1.5rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', fontSize: '1rem' }}>
              Print
            </button>
            {!confirmDuplicate ? (
              <button onClick={() => setConfirmDuplicate(true)} disabled={duplicating}
                style={{ ...btnBase, padding: '0.75rem 1.5rem', backgroundColor: 'var(--color-info)', color: 'var(--text-primary)', fontSize: '1rem' }}>
                Duplicate
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Sure?</span>
                <button onClick={handleDuplicate} disabled={duplicating}
                  style={{ ...btnBase, padding: '0.75rem 1rem', backgroundColor: 'var(--color-info)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                  {duplicating ? 'Duplicating...' : 'Yes, Duplicate'}
                </button>
                <button onClick={() => setConfirmDuplicate(false)}
                  style={{ ...btnBase, padding: '0.75rem 1rem', backgroundColor: 'var(--btn-secondary-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', fontSize: '0.9rem' }}>
                  Cancel
                </button>
              </div>
            )}
            <button onClick={() => setShowEditModal(true)}
              style={{ ...btnBase, padding: '0.75rem 1.5rem', backgroundColor: 'var(--accent-blue)', color: 'var(--text-primary)', fontSize: '1rem' }}>
              Edit Load
            </button>
            <button onClick={onClose}
              style={{ ...btnBase, padding: '0.75rem 2rem', backgroundColor: 'var(--accent-primary)', color: 'var(--text-primary)', fontSize: '1rem' }}>
              Close
            </button>
          </div>
        </div>
        </div>}
      </div>

      {showEditModal && (
        <EditLoadModal load={load} onClose={() => setShowEditModal(false)} onSuccess={() => { setShowEditModal(false); if (onUpdate) onUpdate(); }} />
      )}
      {showRouteMap && (
        <RouteMapModal load={{ ...load, load_stops: loadStops }} onClose={() => setShowRouteMap(false)} />
      )}
      {equipmentModal}
      {showAISuggestions && (
        <AIDispatchSuggestions load={{ ...load, load_stops: loadStops }} onClose={() => setShowAISuggestions(false)} onAssign={() => { setShowAISuggestions(false); if (onUpdate) onUpdate(); }} />
      )}
    </div>
  );
}

export default LoadDetailsModal;
